/**
 * Content pack schema.
 *
 * A pack is the whole authored world as data: which signals exist, where on the dial they sit,
 * when they are on the air, and what they sound like. The engine reads packs; it never contains
 * content. Changing a pack changes the game with no code edit — that is the whole point, and the
 * thing Chapter 2's definition of done tests.
 *
 * **Locale-keyed throughout.** Every player-visible string is a `LocalizedText` map and every
 * speech asset is addressed by locale, so a Turkish pass is a data change: add `"tr"` to
 * `locales`, add `tr` keys, drop files under `audio/tr/`. No engine change, which is exactly what
 * the locked i18n decision asks for.
 *
 * **Asset convention** (locked): `audio/<locale>/<npc>/<node>.ogg`, relative to the pack root.
 * An `audio` source names the npc and node; the loader builds the path. Authors never write URLs,
 * so a locale can be added without touching a single reference.
 *
 * Layout on disk:
 *
 *   content/<pack-id>/pack.json
 *   content/<pack-id>/audio/en/<npc>/<node>.ogg
 *   content/<pack-id>/audio/tr/<npc>/<node>.ogg     (later)
 *
 * The file is in two halves: v1 (Chapter 2) describes a band — signals, placement, schedules.
 * v2 (Chapter 3) adds the story on top of it — clues, gates, dialogue, endings.
 */

/**
 * Current schema version.
 *
 * **v2 (Chapter 3)** adds the story layer: clues, gates, dialogue trees and endings.
 * **v3 (Chapter 6)** adds roaming NPCs: persona, stations, duty cycle and tasks.
 *
 * Every added field is optional, so a v1 pack is a valid v3 pack that tells no story and has
 * nobody on the air — the Chapter 2 sample still loads untouched. The engine accepts all three
 * versions; `PACK_SCHEMA_VERSION` is what new packs should declare.
 */
export const PACK_SCHEMA_VERSION = 3;

/** Versions this engine can read. */
export const SUPPORTED_SCHEMA_VERSIONS = [1, 2, 3] as const;

/** BCP-47-ish locale tag. `en` at launch; `tr` is the planned second pass. */
export type LocaleTag = string;

/**
 * A string in every locale the pack carries. The pack's `defaultLocale` must always be present;
 * everything else may be filled in later, and the loader falls back to the default rather than
 * showing a player an empty label.
 */
export type LocalizedText = Record<LocaleTag, string>;

export interface PackBand {
  /** Lowest tunable frequency, kHz. */
  minKhz: number;
  /** Highest tunable frequency, kHz. */
  maxKhz: number;
}

export interface PackClock {
  /**
   * In-game milliseconds per real millisecond. Omit to take the engine default (12 — one real
   * minute is twelve in-game minutes; see world/clock.ts).
   */
  ratio?: number;
  /** In-game timestamp a fresh world starts at, ms since day 0 00:00. Default: 22:00. */
  startGameMs?: number;
}

/**
 * How the player's transmitter behaves (Chapter 4).
 *
 * These are in-world facts about the equipment, so they belong in the world's data rather than in
 * the engine: a pack set on a handheld with a hot final stage is a different game from one set in
 * a station with a proper rig, and that difference should be authorable. Omitted values take the
 * engine defaults in `voice/transmit.ts`.
 */
export interface PackTransmit {
  /** Longest single transmission, seconds. */
  maxSeconds?: number;
  /** Wait after a normal release, seconds. */
  cooldownSeconds?: number;
  /** Consecutive transmissions before the set needs a longer rest. */
  maxConsecutive?: number;
  /** That longer rest, seconds. */
  overheatedSeconds?: number;
  /** Quiet time after which a burst is forgotten, seconds. */
  burstForgetSeconds?: number;
}

/* ------------------------------------------------------------------------------------------ *
 * Schema v3 — roaming NPCs.
 * ------------------------------------------------------------------------------------------ */

/**
 * How an NPC moves across the band.
 *
 * **Everything here is game-time, and everything is deterministic.** An NPC's position is a pure
 * function of the clock: given the same game time, every client computes the same answer, the
 * validator can sweep a whole night without playing it, and a save resumed a week later finds the
 * NPC exactly where the world says it should be. Nothing consults the wall clock or a random
 * number generator.
 *
 * A strict rotation through the stations would be deterministic too, and would also be obviously
 * mechanical — a player would learn the cycle in two nights. Instead the station for each slot is
 * chosen by hashing the NPC's id with the slot number: a fixed, replayable permutation that does
 * not read as a loop. It is the same trick Chapter 2's band texture uses, for the same reason.
 */
export interface PackRoaming {
  /** Frequencies this NPC works, in kHz. At least one; two or more to actually roam. */
  stations: number[];
  /** How long it stays on one station, in in-game minutes. */
  dwellMinutes: number;
  /**
   * Shifts this NPC's slot boundaries, in in-game minutes. Two NPCs sharing a station list
   * without an offset would move in lockstep, which reads as one thing rather than two.
   */
  offsetMinutes?: number;
  /**
   * Duty cycle, in in-game minutes. An NPC that transmitted continuously would be a carrier, not
   * a person: real operators key up, say something, and listen. Omit for a continuous signal.
   *
   * In-game minutes rather than real seconds so the whole model stays on one clock. At the
   * default 12x ratio, two in-game minutes of transmission is ten real seconds — about the
   * length of a spoken line.
   */
  transmitMinutes?: number;
  /** Quiet between bursts, in in-game minutes. */
  cooldownMinutes?: number;
  /** Audible half-width while broadcasting, kHz. Defaults to the pack's usual signal width. */
  widthKhz?: number;
}

/**
 * Something an NPC is doing, which overrides its wandering.
 *
 * A task pins the NPC to one station for a window — it has business there. Tasks are how an
 * author makes an NPC's movement mean something rather than being texture: "she is at the relay
 * mast between two and four because that is when the tide is out".
 *
 * Tasks are checked in order and the first that applies wins, so the specific goes above the
 * general — the same rule as endings.
 */
export interface PackNpcTask {
  id: string;
  /** Where the NPC is while this task runs. */
  stationKhz: number;
  /** When. Same shape as a signal's schedule. */
  schedule: PackSchedule;
  /** Only while the player holds (or lacks) these. */
  requires?: PackGate;
  /** Dialogue to carry while on this task, overriding the NPC's usual conversation. */
  dialogue?: string;
  notes?: string;
}

export interface PackNpc {
  id: string;
  name: LocalizedText;
  /**
   * Who this is, for whoever writes their lines. Never shown to players — it exists so a second
   * author, or the same author three months later, writes the NPC consistently.
   */
  persona?: string;
  /** Free-form authoring note: which Fish Audio voice rendered this NPC's lines. */
  voice?: string;
  /**
   * Where this NPC is, over time. An NPC with no roaming block never appears on the band by
   * itself — it exists only wherever a signal explicitly places it.
   */
  roaming?: PackRoaming;
  /** Business that overrides the wandering. */
  tasks?: PackNpcTask[];
  /** The conversation this NPC carries while roaming. */
  dialogue?: string;
  /** How the NPC sounds while broadcasting. Defaults to a voice-carrier preset. */
  source?: PackSignalSource;
  /** Shown in the frequency log when the player logs this NPC. */
  label?: LocalizedText;
  /**
   * Only on air when the band is quiet.
   *
   * Coverage NPCs are the answer to a cold start: a server with nobody on it should still feel
   * inhabited. They are gated on the *live listener count*, so they fill an empty band and step
   * back when real people arrive — which is both the honest behaviour and the one that keeps the
   * "was that a person?" question alive.
   */
  coverage?: PackCoverage;
  /** Author-facing description. Never shown to players. */
  notes?: string;
}

/** When a coverage NPC is on the air, in terms of how busy the band actually is. */
export interface PackCoverage {
  /**
   * Only broadcast when at most this many other people are listening on the band. 0 means only
   * when the player is truly alone.
   */
  maxOthers: number;
}

/** A generative source: no asset needed, which is what parasite FX and placeholders want. */
export interface PackSynthSource {
  type: 'synth';
  preset: string;
  /** Preset-specific numbers. Unknown keys are ignored by the engine, not an error. */
  params?: Record<string, number>;
}

/**
 * A pre-rendered audio source, addressed by the locked convention. The loader resolves this to
 * `audio/<locale>/<npc>/<node>.ogg` under the pack root.
 */
export interface PackAudioSource {
  type: 'audio';
  /** Must match a `PackNpc.id`. */
  npc: string;
  /** The line/segment id — the file's basename. */
  node: string;
  loop?: boolean;
  /**
   * Locales this asset has actually been rendered for. Defaults to the pack's `locales`; the
   * validator only looks for files in these, so an in-progress Turkish pass doesn't fail a build.
   */
  locales?: LocaleTag[];
}

export type PackSignalSource = PackSynthSource | PackAudioSource;

export interface PackDrift {
  khzPerGameHour: number;
  /** `window` (default) restarts the drift each time the window opens; `world` never resets. */
  anchor?: 'window' | 'world';
  minKhz?: number;
  maxKhz?: number;
}

export interface PackPlacement {
  frequencyKhz: number;
  /** Half-width of the audible approach, kHz. */
  widthKhz: number;
  drift?: PackDrift;
}

export interface PackGameHourWindow {
  fromHour: number;
  /** Exclusive. May be less than `fromHour` — that is a window across midnight. */
  toHour: number;
}

export interface PackRealAnchor {
  hours?: { from: number; to: number };
  /** 0 = Sunday. */
  weekdays?: number[];
  dateRange?: { fromIso: string; toIso: string };
}

export interface PackSchedule {
  gameHours?: PackGameHourWindow[];
  gameDays?: number[];
  gameSpan?: { fromGameMs: number; toGameMs: number };
  real?: PackRealAnchor;
}

/* ------------------------------------------------------------------------------------------ *
 * Schema v2 — story: clues, gates, dialogue, resolution.
 *
 * Everything below is optional. A v1 pack (Chapter 2) is a valid v2 pack that happens to tell no
 * story, which is what keeps the Chapter 2 sample working untouched.
 * ------------------------------------------------------------------------------------------ */

/**
 * A gate: what the player must already have before something is available to them.
 *
 * The same shape gates signals, dialogue nodes and dialogue options, so "you cannot hear this
 * until you know that" is one concept expressed once. Every clause present must hold.
 *
 * Gates are the only progression mechanism. There is deliberately no scripting language here —
 * an author says what is required, never how to compute it, which is what keeps a pack analysable
 * (the validator can prove the mystery is completable) and safe to load.
 */
export interface PackGate {
  /** Every one of these clues must be held. */
  clues?: string[];
  /** Every one of these events must have fired. */
  events?: string[];
  /** Any one of these clues is enough — for mysteries with more than one route in. */
  anyClues?: string[];
  /** None of these may be held. Used to retire a signal once its purpose is served. */
  notClues?: string[];
  /** None of these may have fired. */
  notEvents?: string[];
}

/** What holding/hearing/answering something gives the player. */
export interface PackEffects {
  /** Clue ids to grant. Granting a clue the player already holds is a no-op, not an error. */
  clues?: string[];
  /** Event ids to fire. */
  events?: string[];
}

/** A thing the player can learn. Clues are the currency of progression. */
export interface PackClue {
  id: string;
  label: LocalizedText;
  /** What the player actually learned. Shown on the clue board. */
  description: LocalizedText;
  /**
   * Which thread of the mystery this belongs to. The truth surface groups by thread, so a player
   * can see *which* part of the story they are short on rather than just a number.
   */
  thread?: string;
  /** Author-facing note. Never shown to players. */
  notes?: string;
}

/** A named thread of the investigation, for grouping clues on the truth surface. */
export interface PackThread {
  id: string;
  label: LocalizedText;
  notes?: string;
}

/**
 * One option a player can take at a dialogue node.
 *
 * Chapter 3 selects options by clicking them. Chapter 4 adds speech: `intents` is the natural
 * language the option-matcher will score an utterance against, authored now so the content does
 * not need revisiting later.
 */
export interface PackDialogueOption {
  id: string;
  /** The option as the player sees it. */
  label: LocalizedText;
  /** Phrasings a player might say to mean this. Unused until Chapter 4; authored from the start. */
  intents?: LocalizedText[];
  /** Hidden entirely unless this gate passes. */
  requires?: PackGate;
  /** Granted when the option is taken. */
  effects?: PackEffects;
  /** Node to move to. Omit to end the conversation. */
  next?: string;
}

export interface PackDialogueNode {
  id: string;
  /** What is said to the player at this node. */
  line: LocalizedText;
  /** The audio for `line`. Omit for a node that is text-only (placeholder while authoring). */
  source?: PackSignalSource;
  /** Hidden unless this gate passes; a conversation arriving here anyway falls through to `next`. */
  requires?: PackGate;
  /** Granted on arrival at this node. */
  effects?: PackEffects;
  options?: PackDialogueOption[];
  /** Where an option-less node goes. Omit for a node that ends the conversation. */
  next?: string;
  /** Author-facing note. Never shown to players. */
  notes?: string;
}

export type DialogueInitiation = 'npc' | 'user';

/** A conversation: a set of nodes with an entry point, attached to a signal. */
export interface PackDialogue {
  id: string;
  /** Must match a `PackNpc.id` when the conversation has a speaker. */
  npc?: string;
  /** Node the conversation opens on. */
  entry: string;
  nodes: PackDialogueNode[];
  notes?: string;
  /**
   * How the conversation is started:
   * - 'npc': NPC broadcasts/transmits first into the channel; player responds.
   * - 'user': Channel is on standby; player must hail/transmit first to get a response.
   * Default: 'npc'
   */
  initiation?: DialogueInitiation;
}

/**
 * How the mystery ends.
 *
 * A story may have several endings; the first whose gate passes, in declaration order, is the one
 * the player reaches. Authors put the most specific ending first.
 */
export interface PackEnding {
  id: string;
  label: LocalizedText;
  /** The resolution text, shown on the truth surface when reached. */
  text: LocalizedText;
  requires: PackGate;
  effects?: PackEffects;
  /**
   * An optional pre-rendered narration of the resolution, played once — dry, not through the
   * radio chain — when the ending is reached. Non-diegetic epilogue in the author's own voice,
   * addressed by the usual convention with a `narrator` path segment (which is why it is not a
   * real NPC and is not validated against `npcs`).
   */
  source?: PackAudioSource;
  notes?: string;
}

/** The story layer of a pack. */
export interface PackStory {
  id: string;
  title: LocalizedText;
  /** Shown on the truth surface before anything has been found. */
  premise?: LocalizedText;
  threads?: PackThread[];
  clues?: PackClue[];
  dialogues?: PackDialogue[];
  endings?: PackEnding[];
  notes?: string;
}

export interface PackSignal {
  /**
   * What this signal sounds like, for captions (Chapter 7).
   *
   * Optional: every synth preset has a generic description, so a signal is captioned the moment
   * it exists. Set this only when the generic one will not do — the preset knows what a beacon
   * sounds like in general, not that *this* beacon has been running since the lighthouse closed.
   */
  caption?: LocalizedText;
  id: string;
  kind: 'story' | 'parasite' | 'npc' | 'ghost';
  /** Shown in the frequency log once the player has logged it (Chapter 3). */
  label?: LocalizedText;
  /** Required when `kind` is `npc`; must match a `PackNpc.id`. */
  npc?: string;
  placement: PackPlacement;
  schedule: PackSchedule;
  /** Tie-break when two signals would share a band. Higher wins. Default 0. */
  priority?: number;
  source: PackSignalSource;

  /* --- v2 story hooks ------------------------------------------------------------------- */

  /**
   * The signal is not on the air at all until this passes. Combined with `schedule` by AND: a
   * gated signal must be both scheduled *and* unlocked. This is how a mystery opens up — a
   * frequency that was dead static last night answers once you know what to listen for.
   */
  requires?: PackGate;
  /** Granted the first time the player actually hears this signal (see `listenSeconds`). */
  effects?: PackEffects;
  /**
   * How long the player must hold the signal before `effects` fire, in real seconds. Stops a
   * blind sweep across the band from collecting a mystery by accident. Default 4.
   */
  listenSeconds?: number;
  /** Conversation this signal carries. Must match a `PackDialogue.id`. */
  dialogue?: string;

  /** Author-facing note. Never shown to players. */
  notes?: string;
}

export interface ContentPack {
  schemaVersion: number;
  id: string;
  title: LocalizedText;
  defaultLocale: LocaleTag;
  locales: LocaleTag[];
  band: PackBand;
  clock?: PackClock;
  /** The player's transmitter. Omit for the engine defaults. */
  transmit?: PackTransmit;
  npcs?: PackNpc[];
  signals: PackSignal[];
  /** The story layer. Absent in a v1 pack, which is then simply a band with no mystery on it. */
  story?: PackStory;
  /** Author-facing metadata. Never shown to players. */
  notes?: string;
}

/** The locked asset path convention, in one place so the loader and validator cannot disagree. */
export function audioAssetPath(locale: LocaleTag, npc: string, node: string): string {
  return `audio/${locale}/${npc}/${node}.ogg`;
}

/**
 * Pick a locale's string, falling back to the pack default. A missing translation should read as
 * untranslated, never as blank.
 */
export function localized(
  text: LocalizedText | undefined,
  locale: LocaleTag,
  defaultLocale: LocaleTag,
): string | undefined {
  if (!text) return undefined;
  return text[locale] ?? text[defaultLocale];
}
